import {SocialMediaLinks} from '@common/ui/footer/footer';
import {BrandLogoImage, Logo} from '@common/ui/navigation/navbar/logo';
import {useLogout} from '@common/auth/requests/use-logout';
import {useAuth} from '@common/auth/use-auth';
import {Button} from '@shadcn/button/button';
import {Dropdown} from '@shadcn/dropdown/dropdown';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {
  premiumPath,
  supportPath,
  usePremiumLocale,
} from '@app/web-player/premium/premium-locale';
import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import {ChevronDownIcon, UserIcon} from 'lucide-react';
import {ReactNode} from 'react';
import {Link, useNavigate} from 'react-router';

type PremiumSiteLayoutProps = {
  children: ReactNode;
  title?: string;
};

export function PremiumSiteLayout({children, title}: PremiumSiteLayoutProps) {
  const {lang, copy} = usePremiumLocale();
  const text = copy.layout;

  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-hidden bg-neutral-950 text-white">
      <PremiumAmbientGlow />
      <div className="relative z-30 flex min-h-screen flex-col">
        {title ? (
          <StaticPageTitle>
            {title}
          </StaticPageTitle>
        ) : null}
        <PremiumSiteHeader lang={lang} text={text} />
        <div className="flex flex-1 flex-col">{children}</div>
        <PremiumSiteFooter lang={lang} text={text} />
      </div>
    </div>
  );
}

function PremiumSiteHeader({
  lang,
  text,
}: {
  lang: Parameters<typeof supportPath>[0];
  text: ReturnType<typeof usePremiumLocale>['copy']['layout'];
}) {
  const {isLoggedIn} = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  const {registration} = useSettings();

  return (
    <header className="w-full bg-black">
      <div className="mx-auto flex h-12 w-full max-w-6xl items-center justify-between gap-3 px-4 sm:h-14 sm:px-6">
        <Logo
          color="light"
          className="h-7 max-h-7 shrink-0 aspect-[516/117] object-contain object-left sm:h-8 sm:max-h-8"
        />
        <nav className="flex items-center justify-end gap-1 sm:gap-1.5">
          <Dropdown.Root>
            <Dropdown.Trigger
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-white hover:bg-white/10 hover:text-white"
                />
              }
            >
              {text.support}
              <ChevronDownIcon className="size-4 opacity-80" />
            </Dropdown.Trigger>
            <Dropdown.Content align="end" className="min-w-44">
              <Dropdown.Item onClick={() => navigate(supportPath(lang))}>
                {text.support}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => navigate('/download')}>
                {text.download}
              </Dropdown.Item>
            </Dropdown.Content>
          </Dropdown.Root>

          <Dropdown.Root>
            <Dropdown.Trigger
              render={
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-9 text-white hover:bg-white/10 hover:text-white"
                  aria-label={text.login}
                />
              }
            >
              <UserIcon className="size-5" />
            </Dropdown.Trigger>
            <Dropdown.Content align="end" className="min-w-44">
              {isLoggedIn ? (
                <>
                  <Dropdown.Item
                    onClick={() => navigate('/account-settings')}
                  >
                    <Trans message="Account" />
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => logout.mutate()}>
                    <Trans message="Log out" />
                  </Dropdown.Item>
                </>
              ) : (
                <>
                  <Dropdown.Item onClick={() => navigate('/login')}>
                    {text.login}
                  </Dropdown.Item>
                  {!registration?.disable ? (
                    <Dropdown.Item onClick={() => navigate('/register')}>
                      {text.signup}
                    </Dropdown.Item>
                  ) : null}
                </>
              )}
            </Dropdown.Content>
          </Dropdown.Root>
        </nav>
      </div>
    </header>
  );
}

function PremiumSiteFooter({
  lang,
  text,
}: {
  lang: Parameters<typeof supportPath>[0];
  text: ReturnType<typeof usePremiumLocale>['copy']['layout'];
}) {
  const mainLinks = [
    {label: text.premiumSubscription, to: premiumPath(lang)},
    {label: text.appDownload, to: '/download'},
    {label: text.contactSupport, to: supportPath(lang)},
  ];
  const legalLinks = [
    {label: text.imprint, to: '/legal/imprint'},
    {label: text.terms, to: '/legal/terms'},
    {label: text.privacyPolicy, to: '/legal/privacy-policy'},
    {label: text.cookies, to: '/legal/cookies'},
  ];

  return (
    <footer className="border-t border-neutral-800 bg-black px-6 py-10">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-8">
        <nav className="flex flex-row flex-wrap items-center justify-center gap-6 text-sm">
          {mainLinks.map(link => (
            <Link key={link.label} className="hover:underline" to={link.to}>
              {link.label}
            </Link>
          ))}
        </nav>

        <SocialMediaLinks className="!flex-row !items-center justify-center md:!flex-row md:!items-center" />

        <div className="flex flex-row flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-neutral-500">
          {legalLinks.map(link =>
            link.to === '#' ? (
              <a key={link.label} href="#" className="hover:underline">
                {link.label}
              </a>
            ) : (
              <Link key={link.label} className="hover:underline" to={link.to}>
                {link.label}
              </Link>
            ),
          )}
          <span className="text-neutral-600">{text.rights}</span>
        </div>
      </div>
    </footer>
  );
}

function PremiumAmbientGlow() {
  return (
    <div className="pointer-events-none isolate">
      <div className="absolute top-0 right-0 left-0 z-10 h-[55vh] min-h-[500px] blur-[80px]">
        <div className="h-full w-full bg-neutral-400 object-cover object-center" />
      </div>
      <div className="absolute top-0 left-0 z-20 h-full w-full bg-linear-to-b from-neutral-950/50 via-neutral-950/80 to-neutral-950" />
    </div>
  );
}

function PremiumFooterLogo() {
  const {branding} = useSettings();
  const src = branding.logo_light || branding.logo_dark;
  if (!src) {
    return null;
  }

  return (
    <Link
      to="/"
      className="block h-10 max-h-10 w-max shrink-0 self-start"
      aria-label={branding.site_name}
    >
      <BrandLogoImage
        src={src}
        className="!h-10 !max-h-10 aspect-[516/117] object-contain object-left"
      />
    </Link>
  );
}
